import React, { PureComponent } from 'react';
import cn from 'classnames';
import { parse, stringify } from 'query-string';
import SVG from 'react-inlinesvg';
import { number, func, bool, string, shape, instanceOf, oneOf } from 'prop-types';
import PageItem from './PageItem';
import { animatedScrollTo } from '../../utils/utils';
import { STATIC_PATH } from '../../config';

const makePageList = (start, finish) => {
  let arr = [];

  for (let i = start; i < finish; i++) {
    arr.push(i);
  }

  return arr;
};

const getPageList = (step, activePage, pageCount, isMobile) => {
  let list = [];

  if (isMobile) {
    if (pageCount <= 3) {
      list = makePageList(1, pageCount + 1);
    } else if (activePage === 1) {
      list = [1, 2, '…', pageCount];
    } else if (activePage === pageCount) {
      list = [1, '…', pageCount - 1, pageCount];
    } else {
      list = [1, '…', activePage, '…', pageCount];
    }
  } else {
    if (pageCount < step * 2 + 6) {
      list = makePageList(1, pageCount + 1);
    } else if (activePage < step * 2 + 1) {
      list = [...makePageList(1, step * 2 + 4), '…', pageCount];
    } else if (activePage > pageCount - step * 2) {
      list = [1, '…', ...makePageList(pageCount - step * 2 - 2, pageCount + 1)];
    } else {
      list = [1, '…', ...makePageList(activePage - step, activePage + step + 1), '…', pageCount];
    }
  }

  return list;
};

const changeUrl = (page) => {
  const parsed = parse(location.search);
  history.pushState({}, null, window.location.pathname + '?' + stringify({ ...parsed, page }));
};

class Pagination extends PureComponent {
  static propTypes = {
    pageCount: number.isRequired,
    activePage: number.isRequired,
    onPageChange: func.isRequired,
    className: string,
    step: number,
    affectUrl: bool,
    scrollToAfterPageChanged: shape({
      ref: oneOf[(instanceOf(HTMLElement), null)],
      duration: number,
      offset: number,
    }),
  };

  static defaultProps = {
    activePage: 1,
    step: 1,
    affectUrl: false,
    onPageChange() {},
  };

  state = {
    isMobile: window.innerWidth < 768,
  };

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    this.setState({
      isMobile: window.innerWidth < 768,
    });
  };

  scrollToAfterChange = ({ ref = null, duration = 400, offset = 0 }) => {
    animatedScrollTo(ref, duration, offset);
  };

  handleClick = (e, page) => {
    e.preventDefault();

    this.props.onPageChange(page - 1);

    const { scrollToAfterPageChanged } = this.props;
    scrollToAfterPageChanged && this.scrollToAfterChange(scrollToAfterPageChanged);

    this.props.affectUrl && changeUrl(page);
  };

  handlePreviousPage = (e) => {
    const { activePage } = this.props;

    if (activePage > 1) {
      this.handleClick(e, activePage - 1);
    }
  };

  handleNextPage = (e) => {
    const { activePage, pageCount } = this.props;

    if (activePage < pageCount) {
      this.handleClick(e, activePage + 1);
    }
  };

  getPageElement = (page, index, pageCount) => (
    <PageItem
      key={index}
      page={page}
      active={this.props.activePage === page}
      onClick={this.handleClick}
      last={index === pageCount - 1}
    />
  );

  renderPagination = () => {
    const { step, activePage, pageCount } = this.props;
    const { isMobile } = this.state;
    const pages = getPageList(step, activePage, pageCount, isMobile);

    return pages.map((page, i) => this.getPageElement(page, i, pageCount));
  };

  render() {
    const { activePage, pageCount } = this.props;
    const paginationClasses = cn('pagination pagination-sm', this.props.className);
    const prevButtonClasses = cn('page-item page-step-link', { disabled: activePage === 1 });
    const nextButtonClasses = cn('page-item page-step-link', {
      disabled: activePage === pageCount,
    });

    return (
      <ul className={paginationClasses}>
        <li className={prevButtonClasses}>
          <a
            role="button"
            className="page-link page-link-arrow-prev"
            onClick={this.handlePreviousPage}
          >
            <SVG
              src={`${STATIC_PATH}/img/action-icons/arrow.svg`}
              height="16"
              width="16"
              cacheRequests
            />
          </a>
        </li>
        {this.renderPagination()}
        <li className={nextButtonClasses}>
          <a role="button" className="page-link page-link-arrow-next" onClick={this.handleNextPage}>
            <SVG
              src={`${STATIC_PATH}/img/action-icons/arrow.svg`}
              height="16"
              width="16"
              cacheRequests
            />
          </a>
        </li>
      </ul>
    );
  }
}

export default Pagination;
